import type { AddressWords } from './address';
import { grandAddress, indicPack, stepNoun, type IndicGrammar, type IndicWords, type UncleKind } from './indic';

/**
 * Punjabi relationship words (PRD 7.2.2), in Gurmukhi and in English letters. Beta until reviewed. Like Hindi,
 * Punjabi names a father's elder brother taya and a younger one chacha; first cousins are "chache da munda"
 * (your chacha's boy), and the possessive da / di / de agrees with the word after it. Every word is checked
 * against the Punjabi University Patiala dictionary, Mahan Kosh or Maya Singh's dictionary (sources table in
 * docs/kinship-languages-plan.md); a spouse's grandparents, uncles and aunts have their own single words there
 * (dadiahura, patrauhra, masehas).
 */

const COUSIN_PA: Record<UncleKind, [string, string]> = {
  FB: ['ਚਾਚੇ ਦਾ ਮੁੰਡਾ', 'ਚਾਚੇ ਦੀ ਕੁੜੀ'],
  FZ: ['ਭੂਆ ਦਾ ਮੁੰਡਾ', 'ਭੂਆ ਦੀ ਕੁੜੀ'],
  MB: ['ਮਾਮੇ ਦਾ ਮੁੰਡਾ', 'ਮਾਮੇ ਦੀ ਕੁੜੀ'],
  MZ: ['ਮਾਸੀ ਦਾ ਮੁੰਡਾ', 'ਮਾਸੀ ਦੀ ਕੁੜੀ'],
};
const COUSIN_LATN: Record<UncleKind, [string, string]> = {
  FB: ['Chache da Munda', 'Chache di Kudi'],
  FZ: ['Bhua da Munda', 'Bhua di Kudi'],
  MB: ['Mame da Munda', 'Mame di Kudi'],
  MZ: ['Masi da Munda', 'Masi di Kudi'],
};
const cousin = (table: Record<UncleKind, [string, string]>) => (kind: UncleKind, sex: 'f' | 'm' | null) => {
  const [m, f] = table[kind];
  return sex === 'f' ? f : sex === 'm' ? m : `${m}/${f}`;
};

const pa: IndicWords = {
  you: 'ਤੁਸੀਂ',
  relative: 'ਰਿਸ਼ਤੇਦਾਰ',
  spouse: { m: 'ਪਤੀ', f: 'ਪਤਨੀ' },
  parent: { m: 'ਪਾਪਾ', f: 'ਮੰਮੀ' },
  child: { m: 'ਪੁੱਤਰ', f: 'ਧੀ' },
  brother: { elder: 'ਵੱਡਾ ਭਰਾ', younger: 'ਛੋਟਾ ਭਰਾ', any: 'ਭਰਾ' },
  sister: { elder: 'ਵੱਡੀ ਭੈਣ', younger: 'ਛੋਟੀ ਭੈਣ', any: 'ਭੈਣ' },
  grandF: { m: 'ਦਾਦਾ', f: 'ਦਾਦੀ' },
  grandM: { m: 'ਨਾਨਾ', f: 'ਨਾਨੀ' },
  greatF: { m: 'ਪੜਦਾਦਾ', f: 'ਪੜਦਾਦੀ' },
  greatM: { m: 'ਪੜਨਾਨਾ', f: 'ਪੜਨਾਨੀ' },
  ancestor: 'ਵਡੇਰਾ',
  grandchildViaSon: { m: 'ਪੋਤਾ', f: 'ਪੋਤੀ' },
  grandchildViaDaughter: { m: 'ਦੋਹਤਾ', f: 'ਦੋਹਤੀ' },
  greatGrandchild: { m: 'ਪੜਪੋਤਾ', f: 'ਪੜਪੋਤੀ' },
  descendant: 'ਵੰਸ਼ਜ',
  uncle: { FB: { elder: 'ਤਾਇਆ', any: 'ਚਾਚਾ' }, FZ: { elder: 'ਭੂਆ', any: 'ਭੂਆ' }, MB: { elder: 'ਮਾਮਾ', any: 'ਮਾਮਾ' }, MZ: { elder: 'ਮਾਸੀ', any: 'ਮਾਸੀ' } },
  uncleSpouse: { FB: { elder: 'ਤਾਈ', any: 'ਚਾਚੀ' }, FZ: { elder: 'ਫੁੱਫੜ', any: 'ਫੁੱਫੜ' }, MB: { elder: 'ਮਾਮੀ', any: 'ਮਾਮੀ' }, MZ: { elder: 'ਮਾਸੜ', any: 'ਮਾਸੜ' } },
  cousin: cousin(COUSIN_PA),
  distantCousin: { m: 'ਦੂਰ ਦਾ ਭਰਾ', f: 'ਦੂਰ ਦੀ ਭੈਣ' },
  nephewViaBrother: { m: 'ਭਤੀਜਾ', f: 'ਭਤੀਜੀ' },
  nephewViaSister: { m: 'ਭਾਣਜਾ', f: 'ਭਾਣਜੀ' },
  parentInLaw: { m: 'ਸਹੁਰਾ', f: 'ਸੱਸ' },
  grandparentInLaw: { m: 'ਦਦਿਅਹੁਰਾ', f: 'ਦਦੇਹਸ' },
  grandparentInLawM: { m: 'ਨਨਿਅਹੁਰਾ', f: 'ਨਨੇਹਸ' },
  spouseUncle: (kind) => ({ FB: 'ਪਤਰੌਹਰਾ', FZ: 'ਫੁਫੇਹਸ', MB: 'ਮਮਿਔਹਰਾ', MZ: 'ਮਸੇਹਸ' })[kind],
  husbandBrother: { elder: 'ਜੇਠ', younger: 'ਦਿਓਰ', any: 'ਜੇਠ/ਦਿਓਰ' },
  husbandSister: 'ਨਨਾਣ',
  wifeBrother: 'ਸਾਲਾ',
  wifeSister: 'ਸਾਲੀ',
  husbandBrotherWife: { elder: 'ਜਿਠਾਣੀ', younger: 'ਦਰਾਣੀ', any: 'ਜਿਠਾਣੀ/ਦਰਾਣੀ' },
  husbandSisterHusband: 'ਨਣਦੋਈਆ',
  wifeSisterHusband: 'ਸਾਂਢੂ',
  wifeBrotherWife: 'ਸਾਲੇਹਾਰ',
  brotherWife: 'ਭਰਜਾਈ',
  sisterHusband: { elder: 'ਜੀਜਾ', younger: 'ਜੀਜਾ', any: 'ਜੀਜਾ' },
  childInLaw: { m: 'ਜਵਾਈ', f: 'ਨੂੰਹ' },
  coParentInLaw: { m: 'ਕੁੜਮ', f: 'ਕੁੜਮਣੀ' },
  stepParent: { m: 'ਮਤਰੇਆ ਪਿਓ', f: 'ਮਤਰੇਈ ਮਾਂ' },
  stepChild: { m: 'ਮਤਰੇਆ ਪੁੱਤਰ', f: 'ਮਤਰੇਈ ਧੀ' },
  stepSibling: { m: 'ਮਤਰੇਆ ਭਰਾ', f: 'ਮਤਰੇਈ ਭੈਣ' },
};

const paLatn: IndicWords = {
  you: 'Tusi',
  relative: 'Rishtedar',
  spouse: { m: 'Pati', f: 'Patni' },
  parent: { m: 'Papa', f: 'Mummy' },
  child: { m: 'Puttar', f: 'Dhee' },
  brother: { elder: 'Vadda Bhra', younger: 'Chhota Bhra', any: 'Bhra' },
  sister: { elder: 'Vaddi Bhain', younger: 'Chhoti Bhain', any: 'Bhain' },
  grandF: { m: 'Dada', f: 'Dadi' },
  grandM: { m: 'Nana', f: 'Nani' },
  greatF: { m: 'Pardada', f: 'Pardadi' },
  greatM: { m: 'Parnana', f: 'Parnani' },
  ancestor: 'Vadera',
  grandchildViaSon: { m: 'Pota', f: 'Poti' },
  grandchildViaDaughter: { m: 'Dohta', f: 'Dohti' },
  greatGrandchild: { m: 'Parpota', f: 'Parpoti' },
  descendant: 'Vanshaj',
  uncle: { FB: { elder: 'Taya', any: 'Chacha' }, FZ: { elder: 'Bhua', any: 'Bhua' }, MB: { elder: 'Mama', any: 'Mama' }, MZ: { elder: 'Masi', any: 'Masi' } },
  uncleSpouse: { FB: { elder: 'Tai', any: 'Chachi' }, FZ: { elder: 'Phuphad', any: 'Phuphad' }, MB: { elder: 'Mami', any: 'Mami' }, MZ: { elder: 'Masad', any: 'Masad' } },
  cousin: cousin(COUSIN_LATN),
  distantCousin: { m: 'Door da Bhra', f: 'Door di Bhain' },
  nephewViaBrother: { m: 'Bhatija', f: 'Bhatiji' },
  nephewViaSister: { m: 'Bhanja', f: 'Bhanji' },
  parentInLaw: { m: 'Sauhra', f: 'Sass' },
  grandparentInLaw: { m: 'Dadiahura', f: 'Dadehas' },
  grandparentInLawM: { m: 'Naniahura', f: 'Nanehas' },
  spouseUncle: (kind) => ({ FB: 'Patrauhra', FZ: 'Phuphehas', MB: 'Mamiauhra', MZ: 'Masehas' })[kind],
  husbandBrother: { elder: 'Jeth', younger: 'Diyor', any: 'Jeth/Diyor' },
  husbandSister: 'Nanaan',
  wifeBrother: 'Sala',
  wifeSister: 'Sali',
  husbandBrotherWife: { elder: 'Jithani', younger: 'Darani', any: 'Jithani/Darani' },
  husbandSisterHusband: 'Nandoia',
  wifeSisterHusband: 'Saandhu',
  wifeBrotherWife: 'Salehar',
  brotherWife: 'Bharjai',
  sisterHusband: { elder: 'Jija', younger: 'Jija', any: 'Jija' },
  childInLaw: { m: 'Javai', f: 'Nooh' },
  coParentInLaw: { m: 'Kuram', f: 'Kurmani' },
  stepParent: { m: 'Matreya Pyo', f: 'Matrei Maa' },
  stepChild: { m: 'Matreya Puttar', f: 'Matrei Dhee' },
  stepSibling: { m: 'Matreya Bhra', f: 'Matrei Bhain' },
};

/** Possessives agree with the next noun: da (m), di (f), de (honorific, or before another possessive). */
function grammar(latin: boolean): IndicGrammar {
  const w = (word: string, oblique = word, honorific = false) => ({ word, oblique, honorific });
  const forms = latin
    ? {
        parent: { m: w('papa', 'papa', true), f: w('mummy') },
        child: { m: w('puttar'), f: w('dhee') },
        sibling: { m: w('bhra'), f: w('bhain') },
        spouse: { m: w('pati', 'pati', true), f: w('patni') },
      }
    : {
        parent: { m: w('ਪਾਪਾ', 'ਪਾਪਾ', true), f: w('ਮੰਮੀ') },
        child: { m: w('ਪੁੱਤਰ'), f: w('ਧੀ') },
        sibling: { m: w('ਭਰਾ'), f: w('ਭੈਣ') },
        spouse: { m: w('ਪਤੀ', 'ਪਤੀ', true), f: w('ਪਤਨੀ') },
      };
  const [da, di, de] = latin ? ['da', 'di', 'de'] : ['ਦਾ', 'ਦੀ', 'ਦੇ'];
  return {
    noun: (step) => stepNoun(step, forms),
    your: (fem) => (latin ? (fem ? 'tuhadi' : 'tuhade') : fem ? 'ਤੁਹਾਡੀ' : 'ਤੁਹਾਡੇ'),
    of: (fem, last, honorific) => (fem ? di : honorific || !last ? de : da),
    attach: false,
    generation: (delta) => {
      if (delta === 0) return latin ? 'Tuhadi peerhi' : 'ਤੁਹਾਡੀ ਪੀੜ੍ਹੀ';
      if (delta === -1) return latin ? 'Maape' : 'ਮਾਪੇ';
      if (delta === 1) return latin ? 'Aulad' : 'ਔਲਾਦ';
      const n = Math.abs(delta);
      if (latin) return `${n} peerhian ${delta < 0 ? 'upar' : 'thalle'}`;
      return `${n} ਪੀੜ੍ਹੀਆਂ ${delta < 0 ? 'ਉੱਪਰ' : 'ਥੱਲੇ'}`;
    },
  };
}

/**
 * Forms of address (PRD 7.2.4). An elder brother is Veer ji, Bha ji (said "Paa ji"; not ਪਾਜੀ "scoundrel" nor ਭਾਜੀ, a
 * vegetable dish), Bhai sahib or Bhra ji; an elder sister Bhain ji, Didi or Bibi; younger ones by name (Koul & Bala
 * 1989). ਜੀ goes with kin terms and names alike; ਭਾਈ takes ਸਾਹਿਬ, not ਜੀ. A mother-in-law is ਬੇਬੇ (Maya Singh).
 * Sources: docs/kinship-languages-plan.md 16.3 and 16.7.
 */
const paAddress: AddressWords = {
  nameScript: /^\p{Script=Gurmukhi}+$/u,
  parent: { m: ['ਪਾਪਾ ਜੀ', 'ਬਾਪੂ ਜੀ', 'ਭਾਪਾ ਜੀ'], f: ['ਮੰਮੀ ਜੀ', 'ਬੇਬੇ ਜੀ', 'ਬੀਬੀ ਜੀ'] },
  grandparent: grandAddress([['ਦਾਦਾ ਜੀ'], ['ਦਾਦੀ ਜੀ'], ['ਨਾਨਾ ਜੀ'], ['ਨਾਨੀ ਜੀ']], [['ਪੜਦਾਦਾ ਜੀ'], ['ਪੜਦਾਦੀ ਜੀ'], ['ਪੜਨਾਨਾ ਜੀ'], ['ਪੜਨਾਨੀ ਜੀ']]),
  elder: { m: ['ਵੀਰ ਜੀ', 'ਭਾ ਜੀ', 'ਭਾਈ ਸਾਹਿਬ', 'ਭਰਾ ਜੀ', '{name} ਵੀਰ ਜੀ'], f: ['ਭੈਣ ਜੀ', 'ਦੀਦੀ', 'ਬੀਬੀ', '{name} ਭੈਣ ਜੀ'] },
  uncle: { FB: { elder: ['ਤਾਇਆ ਜੀ'], any: ['ਚਾਚਾ ਜੀ'] }, FZ: { any: ['ਭੂਆ ਜੀ'] }, MB: { any: ['ਮਾਮਾ ਜੀ'] }, MZ: { any: ['ਮਾਸੀ ਜੀ'] } },
  uncleSpouse: { FB: { elder: ['ਤਾਈ ਜੀ'], any: ['ਚਾਚੀ ਜੀ'] }, FZ: { any: ['ਫੁੱਫੜ ਜੀ'] }, MB: { any: ['ਮਾਮੀ ਜੀ'] }, MZ: { any: ['ਮਾਸੜ ਜੀ'] } },
  parentInLaw: { m: ['ਪਾਪਾ ਜੀ', 'ਬਾਪੂ ਜੀ'], f: ['ਬੇਬੇ ਜੀ', 'ਮੰਮੀ ਜੀ'] },
  elderInLaw: (sex, spouseSex) =>
    spouseSex === 'm' ? (sex === 'f' ? ['ਭੈਣ ਜੀ', 'ਬੀਬੀ ਜੀ'] : ['ਭਾਈ ਸਾਹਿਬ', 'ਵੀਰ ਜੀ', 'ਭਰਾ ਜੀ']) : sex === 'f' ? ['ਭੈਣ ਜੀ', 'ਬੀਬੀ'] : ['ਵੀਰ ਜੀ', 'ਭਾਈ ਸਾਹਿਬ'],
  elderSiblingSpouse: { m: ['ਜੀਜਾ ਜੀ'], f: ['ਭਾਬੀ ਜੀ', 'ਭਰਜਾਈ ਜੀ'] },
  coSiblingInLaw: (sex) => (sex === 'f' ? ['ਭਾਬੀ ਜੀ', 'ਦੀਦੀ', 'ਭੈਣ ਜੀ'] : sex === 'm' ? ['ਜੀਜਾ ਜੀ'] : []),
  coParentInLaw: { m: ['ਕੁੜਮ ਜੀ'], f: ['ਕੁੜਮਣੀ ਜੀ'] },
};
const paLatnAddress: AddressWords = {
  parent: { m: ['Papa ji', 'Bapu ji', 'Bhapa ji'], f: ['Mummy ji', 'Bebe ji', 'Bibi ji'] },
  grandparent: grandAddress([['Dada ji'], ['Dadi ji'], ['Nana ji'], ['Nani ji']], [['Pardada ji'], ['Pardadi ji'], ['Parnana ji'], ['Parnani ji']]),
  elder: { m: ['Veer ji', 'Bha ji', 'Paa ji', 'Bhai sahib', 'Bhra ji', '{name} veer ji'], f: ['Bhain ji', 'Didi', 'Bibi', '{name} bhain ji'] },
  uncle: { FB: { elder: ['Taya ji'], any: ['Chacha ji'] }, FZ: { any: ['Bhua ji'] }, MB: { any: ['Mama ji'] }, MZ: { any: ['Masi ji'] } },
  uncleSpouse: { FB: { elder: ['Tai ji'], any: ['Chachi ji'] }, FZ: { any: ['Phuphad ji'] }, MB: { any: ['Mami ji'] }, MZ: { any: ['Masad ji'] } },
  parentInLaw: { m: ['Papa ji', 'Bapu ji'], f: ['Bebe ji', 'Mummy ji'] },
  elderInLaw: (sex, spouseSex) =>
    spouseSex === 'm' ? (sex === 'f' ? ['Bhain ji', 'Bibi ji'] : ['Bhai sahib', 'Veer ji', 'Bhra ji']) : sex === 'f' ? ['Bhain ji', 'Bibi'] : ['Veer ji', 'Bhai sahib'],
  elderSiblingSpouse: { m: ['Jija ji'], f: ['Bhabi ji', 'Bharjai ji'] },
  coSiblingInLaw: (sex) => (sex === 'f' ? ['Bhabi ji', 'Didi', 'Bhain ji'] : sex === 'm' ? ['Jija ji'] : []),
  coParentInLaw: { m: ['Kuram ji'], f: ['Kurmani ji'] },
};

export const paPack = indicPack({ id: 'pa', name: 'ਪੰਜਾਬੀ', lang: 'pa' }, pa, grammar(false), false, paAddress);
export const paLatnPack = indicPack({ id: 'pa-Latn', name: 'Punjabi (chacha, masi)', lang: 'pa-Latn' }, paLatn, grammar(true), true, paLatnAddress);
